<template>
  <v-container>
    <SimplePageHeader title="Deal Details" class="px-6 pr-6 mb-5">
      <template v-slot:right-buttons>
        <v-btn
          color="primary"
          @click="showJobOpeningInfoModal"
          v-if="zohoIntegrationEnabled && deal.dealCreationTypeId === CREATE_FROM_ZOHO"
          block
        >
          Job Opening Info.
        </v-btn>
      </template>
    </SimplePageHeader>
    <v-stepper
      :value="currentStep"
    >
    <!-- Stepper Header -->
    <div class="stepper-header-container">
      <v-btn icon class="stepper-scroll-btn" @click="scrollStepperLeft">
        <v-icon>chevron_left</v-icon>
      </v-btn>
      <div ref="stepperScroll" class="stepper-scroll-wrapper">
        <v-stepper-header>
        <v-stepper-step
          v-if="zohoIntegrationEnabled"
          :step="CREATION_TYPE"
          :complete="stepStatuses[CREATION_TYPE]"
          :editable="stepStatuses[CREATION_TYPE]"
          @click="goToStep(CREATION_TYPE)"
        >
          <span :style="currentStep === CREATION_TYPE ? selectedStepStyle :''">Creation Type</span>
        </v-stepper-step>

        <v-divider v-if="zohoIntegrationEnabled"></v-divider>

        <v-stepper-step
          :step="CONTRACT_TYPE"
          :complete="stepStatuses[CONTRACT_TYPE]"
          :editable="stepStatuses[CONTRACT_TYPE]"
          @click="goToStep(CONTRACT_TYPE)"
        >
          <span :style="currentStep === CONTRACT_TYPE ? selectedStepStyle:''">Deal Type</span>
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step
          :step="CLIENT"
          :complete="stepStatuses[CLIENT]"
          :editable="stepStatuses[CLIENT]"
          @click="goToStep(CLIENT)"
        >
          <span :style="currentStep === CLIENT ? selectedStepStyle:''">Client</span>
        </v-stepper-step>

        <v-divider v-if="deal.dealAssignmentType === PROJECT"></v-divider>

        <v-stepper-step
          v-if="deal.dealAssignmentType === PROJECT"
          :step="PROJECT_INFO"
          :complete="stepStatuses[PROJECT_INFO]"
          :editable="stepStatuses[PROJECT_INFO]"
          @click="goToStep(PROJECT_INFO)"
        >
          <span :style="currentStep === PROJECT_INFO ? selectedStepStyle :''">Project</span>
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step
          :step="CANDIDATE"
          :complete="stepStatuses[CANDIDATE]"
          :editable="stepStatuses[CANDIDATE]"
          @click="goToStep(CANDIDATE)"
        >
          <span :style="currentStep === CANDIDATE ? selectedStepStyle:''">Candidate</span>
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step
          :step="CONTRACT_DETAILS"
          :complete="stepStatuses[CONTRACT_DETAILS]"
          :editable="stepStatuses[CONTRACT_DETAILS]"
          @click="goToStep(CONTRACT_DETAILS)"
        >
          <span :style="currentStep === CONTRACT_DETAILS ? selectedStepStyle:''">Contract Details</span>
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step
          :step="SUMMARY"
          :complete="stepStatuses[SUMMARY]"
          :editable="stepStatuses[SUMMARY]"
          @click="goToStep(SUMMARY)"
        >
          <span :style="currentStep === SUMMARY ? selectedStepStyle:''">Summary</span>
        </v-stepper-step>

        </v-stepper-header>
      </div>
      <v-btn icon class="stepper-scroll-btn" @click="scrollStepperRight">
        <v-icon>chevron_right</v-icon>
      </v-btn>
    </div>

      <!-- Stepper Items -->
      <v-stepper-items>
        <v-stepper-content :step="CREATION_TYPE">
          <DealCreationType
            ref="dealCreationType"
            :readonly="isReadOnlyAccess"
            @setDealCreationType="setDealCreationType"
            @importFromZoho="handleImportFromZoho"
            @complete="markAllStepsAsCompleted"
          />
        </v-stepper-content>

        <v-stepper-content :step="CONTRACT_TYPE">
          <DealAssignmentType
            ref="dealAssignmentType"
            :readonly="isReadOnlyAccess"
            :disableProjectOption="deal.dealCreationTypeId === CREATE_FROM_ZOHO"
            @setDealAssignmentType="setDealAssignmentType"
          />
        </v-stepper-content>

        <v-stepper-content :step="CLIENT">
          <DealClient
            ref="dealClient"
            :client="deal.client"
            :readonly="isReadOnlyAccess"
            @setClientTypeId="setClientTypeId"
            @setClientName="setClientName"
            @setBusinessEntityId="setBusinessEntityId"
            @setExistingClientId="setExistingClientId"
            @setInvoiceFrequencyId="setInvoiceFrequencyId"
            @setThirdPartyPortalId="setThirdPartyPortalId"
            @setVmsFee="setVmsFee"
            @setUseStaffLynxForTimeEntering="setUseStaffLynxForTimeEntering"
          />
        </v-stepper-content>

        <v-stepper-content :step="PROJECT_INFO">
          <DealProjectStep
            ref="dealProjectStep"
            :project="deal.project"
            :readonly="isReadOnlyAccess"
            @setProjectName="setProjectName"
          />
        </v-stepper-content>

        <v-stepper-content :step="CANDIDATE">
          <DealSingleCandidate
            v-if="deal.dealAssignmentType !== PROJECT"
            ref="dealSingleCandidate"
            :readonly="isReadOnlyAccess"
            :candidate="deal.candidates[0]"
            @setCandidateTypeId="setCandidateTypeId"
            @setExistingCandidateId="setExistingCandidateId"
            @setFirstName="setFirstName"
            @setLastName="setLastName"
            @setEmailAddress="setEmailAddress"
          />
          <DealMultipleCandidates
            v-else
            ref="dealMultipleCandidates"
            :candidates="deal.candidates"
            :readonly="isReadOnlyAccess"
            @addCandidate="addCandidateHandler"
            @deleteCandidate="deleteCandidateHandler"
            @setCandidateTypeId="setCandidateTypeId"
            @setExistingCandidateId="setExistingCandidateId"
            @setFirstName="setFirstName"
            @setLastName="setLastName"
            @setEmailAddress="setEmailAddress"
          />
        </v-stepper-content>

        <v-stepper-content :step="CONTRACT_DETAILS">
          <DealContract
            v-if="deal.dealAssignmentType === CONTRACTOR"
            ref="dealContract"
            :candidateId="deal.candidates[0].id"
            :readonly="isReadOnlyAccess"
            :workedHoursAverageDefault="payRateSettings.workedHoursAverageDefault"
            :overtimeThresholdDefault="payRateSettings.overtimeThresholdDefault"
            :overtimeRateDefault="payRateSettings.overtimeRateDefault"
            @setJobTitle="setJobTitle"
            @setWorkedHoursAverage="setWorkedHoursAverage"
            @setPayFrequencyId="setPayFrequencyId"
            @setContractTypeId="setContractTypeId"
            @setLengthOfContract="setLengthOfContract"
            @setConversionFee="setConversionFee"
            @setStartAt="setStartAt"
            @setEndAt="setEndAt"
            @setEmployeeTypeId="setEmployeeTypeId"
            @setCorporateName="setCorporateName"
            @setEin="setEin"
            @setPayRate="setPayRate"
            @setPayRateTypeId="setPayRateTypeId"
            @setBillRate="setBillRate"
            @setBillRateTypeId="setBillRateTypeId"
            @setOvertimeEligible="setOvertimeEligible"
            @setOvertimeThreshold="setOvertimeThreshold"
            @setOvertimeRate="setOvertimeRate"
            @setTimeSheetType="setTimeSheetType"
            @setClockIPAddress="setClockIPAddress"
            @setCommissions="setCommissions"
            @setExpenses="setExpenses"
            @setContractorCommissionEnabled="setContractorCommissionEnabled"
            @setContractorCommissionMarkup="setContractorCommissionMarkup"
          />
          <DealProject
            v-else-if="deal.dealAssignmentType === PROJECT"
            ref="dealProject"
            :candidates="deal.candidates"
            :workedHoursAverageDefault="payRateSettings.workedHoursAverageDefault"
            :overtimeThresholdDefault="payRateSettings.overtimeThresholdDefault"
            :overtimeRateDefault="payRateSettings.overtimeRateDefault"
            :readonly="isReadOnlyAccess"
            @setJobTitle="setJobTitle"
            @setWorkedHoursAverage="setWorkedHoursAverage"
            @setPayFrequencyId="setPayFrequencyId"
            @setContractTypeId="setContractTypeId"
            @setLengthOfContract="setLengthOfContract"
            @setConversionFee="setConversionFee"
            @setStartAt="setStartAt"
            @setEndAt="setEndAt"
            @setEmployeeTypeId="setEmployeeTypeId"
            @setCorporateName="setCorporateName"
            @setEin="setEin"
            @setPayRate="setPayRate"
            @setPayRateTypeId="setPayRateTypeId"
            @setBillRate="setBillRate"
            @setBillRateTypeId="setBillRateTypeId"
            @setOvertimeEligible="setOvertimeEligible"
            @setOvertimeThreshold="setOvertimeThreshold"
            @setOvertimeRate="setOvertimeRate"
            @setTimeSheetType="setTimeSheetType"
            @setClockIPAddress="setClockIPAddress"
            @setCommissions="setCommissions"
            @setExpenses="setExpenses"
            @setContractorCommissionEnabled="setContractorCommissionEnabled"
            @setContractorCommissionMarkup="setContractorCommissionMarkup"
          />
          <DealPermanentPlacement
            v-else
            ref="dealPermanentPlacement"
            :readonly="isReadOnlyAccess"
            :candidateId="deal.candidates[0].id"
            @setJobTitle="setJobTitle"
            @setPermPlacementDate="setPermPlacementDate"
            @setPermPlacementSalary="setPermPlacementSalary"
            @setPermPlacementFee="setPermPlacementFee"
            @setPermPlacementFeeTypeId="setPermPlacementFeeTypeId"
            @setPermPlacementInvoiceDate="setPermPlacementInvoiceDate"
            @setPermPlacementInvoiceDueDate="setPermPlacementInvoiceDueDate"
            @setCommissions="setCommissions"
            @setExpenses="setExpenses"
          />
        </v-stepper-content>

        <v-stepper-content :step="SUMMARY">
          <DealSummary
            :readonly="isReadOnlyAccess"
            @submitDeal="submitDealHandler"
            @approveDeal="approveDealHandler"
            @rejectDeal="rejectDealHandler"
          />
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
    <!-- Navigation Buttons -->
    <v-row justify="space-between" class="mt-1">
      <v-col
        cols="6"
        >
        <v-btn
          v-if="showSaveAndExitButton"
          width="10em"
          color="secondary"
          class="mr-4"
          @click="saveAndExit"
          >
          Save & Exit
        </v-btn>
      </v-col>
      <v-col
        cols="6"
        class="text-right"
      >
        <v-btn
          color="secondary"
          class="mr-4"
          width="10em"
          outlined
          :disabled="!showGoBackButton"
          @click="goBack"
        >
          Back
        </v-btn>
        <v-btn
          color="primary"
          width="10em"
          :disabled="!showNextStepButton"
          @click="nextStepAndSave"
        >
          Next
        </v-btn>
      </v-col>
    </v-row>
    <DynamicModal
      :showModal="showErrorMessage"
      :persistent="false"
      @clickOutside="closeErrorMessage"
      title="Missing Required Fields"
      maxWidth="35em"
    >
      <v-container fluid slot="title-buttons" class="py-1">
        <v-row justify="end">
          <v-btn
            icon
            depressed
            color="primary"
            @click="closeErrorMessage"
          >
            <v-icon color="white">close</v-icon>
          </v-btn>
        </v-row>
      </v-container>
      <v-container fluid slot="content">
        <div v-html="errorMessages"></div>
      </v-container>
      <v-container fluid slot="buttons" class="pb-0 mb-0">
        <v-row>
          <v-col cols="12">
            <v-btn
              block
              depressed
              outlined
              @click="closeErrorMessage"
            >
              Back
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </DynamicModal>
    <ZRJobOpeningsInfoModal
      ref="ZRJobOpeningsInfoModal"
      :jobOpeningInfo="jobOpeningInfo"
    ></ZRJobOpeningsInfoModal>
    <MessageBox
      ref="messageBox"
      :message="stepErrorMessage"
      color="error"
      closeTextColor="white"
    ></MessageBox>
  </v-container>
</template>

<script>
import {
  DealCreationType,
  DealAssignmentType,
  DealClient,
  DealProjectStep,
  ZRJobOpeningsInfoModal
} from '@/components/dealsV2/screens'
import { SimplePageHeader } from '@/components/pageComponents'
import DynamicModal from '@/components/dialogs/DynamicModal'
import { DealSingleCandidate, DealMultipleCandidates } from './screens/dealCandidateScreens'
import { DealContract, DealPermanentPlacement, DealProject } from './screens/dealAssignmentTypeScreens'
import { mapFields } from '@/vueHelpers/vuex'
import { mapActions } from 'vuex'
import { dealStatuses } from '@/lib/enum/dealStatuses'
import { dealWizardSteps, dealWizardStepsStatus } from '@/lib/constants/dealWizardSteps'
import { placementsRoute } from '@/lib/constants/routes'
import { CONTRACTOR, PERMANENT_PLACEMENT, PROJECT } from '@/lib/constants/dealAssignmentTypes'
import DealSummary from './screens/dealSummaryScreens/DealSummary'
import {
  dealCreationTypeMethods,
  zohoImportMethods,
  dealAssignmentTypeMethods,
  dealClientMethods,
  dealProjectMethods,
  dealCandidateMethods,
  dealContractDetailsMethods,
  dealPermPlacementMethods,
  dealSummaryMethods
} from './DealWizardContainer'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'
import { getCurrentUser, userHasRoles } from '@/lib/authentication'
import { wordsSimilarity } from '@/lib/miscellaneous'
import { HOURLY as HOURLY_PAY_RATE } from '@/lib/constants/payRateTypes'
import { HOURLY as HOURLY_BILL_RATE } from '@/lib/constants/billRateTypes'
import { CONTRACT_TO_HIRE } from '@/lib/constants/contractTypes'
import { CREATE_FROM_ZOHO } from '@/lib/constants/dealCreationTypes'
import { MessageBox } from '@/components/shared/messages'

export default {
  components: {
    DealCreationType,
    DealAssignmentType,
    DealClient,
    DealProjectStep,
    DealSingleCandidate,
    DealMultipleCandidates,
    DealContract,
    DealPermanentPlacement,
    DealProject,
    DealSummary,
    DynamicModal,
    ZRJobOpeningsInfoModal,
    SimplePageHeader,
    MessageBox
  },
  data () {
    return {
      selectedStepStyle: 'border-bottom: 2px solid #1976d2; padding-bottom: 2px;',
      currentStep: dealWizardSteps.CREATION_TYPE,
      // Error message modal shown when missing required fields
      showErrorMessage: false,
      errorMessages: '',

      // Deal Steps
      ...dealWizardSteps,

      // Stepper status - False means the step is not completed yet.
      // Each stepStatuses becomes editable when the previous step is completed.
      stepStatuses: { ...dealWizardStepsStatus },

      // Job Opening Information -
      // This information is only relevant when the deal is created from a Zoho job opening.
      jobOpeningInfo: {
        client_Name: {
          name: ''
        },
        associated_Candidates: [],
        posting_Title: '',
        job_Type: '',
        salary: '',
        hourly_Rate: '',
        client_Rate: '',
        created_By: {
          name: ''
        },
        account_Manager: {
          name: ''
        },
        duration: '',
        duration_Type: ''
      },

      // Domain constants
      CREATE_FROM_ZOHO,
      PROJECT,
      CONTRACTOR,
      PERMANENT_PLACEMENT,
      HOURLY_PAY_RATE,
      HOURLY_BILL_RATE,
      CONTRACT_TO_HIRE,

      // Step validators
      // This object contains the validator for each step with their
      // own error messages.
      stepValidators: {
        [dealWizardSteps.CREATION_TYPE]: () => {
          return this.$refs.dealCreationType.isCreationTypeValid()
        },
        [dealWizardSteps.CONTRACT_TYPE]: () => {
          return this.$refs.dealAssignmentType.isAssignmentTypeValid()
        },
        [dealWizardSteps.CLIENT]: () => {
          return this.$refs.dealClient.isClientValid()
        },
        [dealWizardSteps.PROJECT_INFO]: () => {
          return this.$refs.dealProjectStep.isProjectStepValid()
        },
        [dealWizardSteps.CANDIDATE]: () => {
          if (this.deal.dealAssignmentType === PROJECT) {
            return this.$refs.dealMultipleCandidates.areCandidatesValid()
          } else {
            return this.$refs.dealSingleCandidate.isCandidateValid()
          }
        },
        [dealWizardSteps.CONTRACT_DETAILS]: () => {
          if (this.deal.dealAssignmentType === CONTRACTOR) {
            return this.$refs.dealContract.isContractValid()
          } else if (this.deal.dealAssignmentType === PERMANENT_PLACEMENT) {
            return this.$refs.dealPermanentPlacement.isPermanentPlacementValid()
          } else if (this.deal.dealAssignmentType === PROJECT) {
            return this.$refs.dealProject.isProjectValid()
          }
        }
      },
      // Step error message
      stepErrorMessage: 'There are required fields missing'
    }
  },
  computed: {
    ...mapFields('settings', [
      'zohoIntegrationEnabled',
      'payRateSettings'
    ]),
    ...mapFields('dealV2', [
      'deal'
    ]),
    dealIdParameter () {
      return this.$route.params.id
    },
    showSaveAndExitButton () {
      return !this.isReadOnlyAccess &&
      // Do not show the Save & Exit button when
      // the deal creation type is not selected,
      // since this is the only required fields of
      // deals table
      !!this.deal.dealCreationTypeId
    },
    showGoBackButton () {
      return this.currentStep !== this.CREATION_TYPE
    },
    showNextStepButton () {
      // If the current step is not the summary
      return this.currentStep !== this.SUMMARY &&
      // Disable the next button when
      // the deal creation type is not selected,
      // since clicking the button will try to save
      // the deal and the backend will return an error
      !!this.deal.dealCreationTypeId
    },
    isApproved () {
      return this.deal.statusId === dealStatuses.Approved
    },
    isSubmitted () {
      return this.deal.statusId === dealStatuses.New
    },
    isReadOnlyAccess () {
      // If user is not admin, manager, or sales person.
      return !userHasRoles({
        user: getCurrentUser(),
        allowedRoles: [ADMIN, MANAGER, SALES_PERSON] }
      ) ||
      // If the deal is already approved
      this.isApproved ||
      // If the deal is submitted but the user is not admin
      (this.isSubmitted && !userHasRoles({
        user: getCurrentUser(),
        allowedRoles: [ADMIN] }
      ))
    }
  },
  methods: {
    wordsSimilarity,
    ...mapActions('dealV2', [
      'getDeal',
      'addCandidate',
      'deleteCandidate',
      'saveOrUpdateDeal',
      'submitDeal',
      'approveDeal',
      'rejectDeal',
      'clearDeal'
    ]),
    ...mapActions('settings', ['getSettings']),
    ...mapActions('clients', ['getClients']),
    ...mapActions('users', ['getUsers']),
    ...mapActions('client', ['getClient']),
    ...mapActions('user', ['getUser']),

    // Deal Wizard Container Methods
    async goBack () {
      if (this.showGoBackButton) {
        // When on the project contract details step, try to navigate to the previous
        // candidate first. Only go back to the previous wizard step when already
        // on the first candidate.
        if (this.deal.dealAssignmentType === PROJECT &&
          this.currentStep === dealWizardSteps.CONTRACT_DETAILS &&
          this.$refs.dealProject &&
          this.$refs.dealProject.goToPreviousCandidate()) {
          await this.save()
          return
        }
        this.currentStep--
        // This will ensure that when project type is not selected, the project step will be skipped
        if (this.currentStep === this.PROJECT_INFO && this.deal.dealAssignmentType !== PROJECT) {
          this.currentStep--
        }
        await this.save()
      }
    },
    allowNextStep () {
      // Check if the current step has the necessary information to move forward
      return this.stepValidators[this.currentStep]()
    },
    nextStep () {
      if (this.showNextStepButton) {
        this.currentStep++
        // This will ensure that when project type is not selected, the project step will be skipped
        if (this.currentStep === this.PROJECT_INFO && this.deal.dealAssignmentType !== PROJECT) {
          this.currentStep++
        }
      }
    },
    async nextStepAndSave () {
      const allowNextStep = this.allowNextStep()
      if (this.deal.dealCreationTypeId === CREATE_FROM_ZOHO ||
        allowNextStep === true) {
        this.nextStep()
        await this.save()
      } else if (allowNextStep === null) {
        // A candidate was navigated within the step — just save, stay on step
        await this.save()
      } else {
        this.stepErrorMessage = allowNextStep
        this.$refs.messageBox.show()
      }
    },
    async goToStep (step) {
      step = Number(step)
      const previousStep = Number(this.currentStep)

      // Zoho deals allow free navigation between all steps without validation.
      if (this.deal.dealCreationTypeId === CREATE_FROM_ZOHO) {
        this.currentStep = step
        await this.save()
        return
      }

      // A step is navigable if it is:
      // - the current step (no-op click)
      // - a completed step (user already passed it)
      // - the immediate next step of a completed step (next in sequence)
      const canNavigateToStep =
        step === previousStep ||
        this.stepStatuses[step] ||
        (step > 1 && this.stepStatuses[step - 1])

      if (!canNavigateToStep) {
        // The target step is not yet reachable. Reset Vuetify's internal stepper
        // state back to the current step. We must set null first so Vue detects
        // an actual value change (null → previousStep), which forces the stepper
        // to re-sync its internal state. Without this, if currentStep already
        // equals previousStep, Vue skips the update and the stepper stays on the
        // wrong step visually.
        this.currentStep = null
        await this.$nextTick()
        this.currentStep = previousStep
      } else if (step > previousStep) {
        // Moving forward: validate the current step before allowing the transition.
        const allowNextStep = this.allowNextStep()

        if (allowNextStep === null) {
          // A candidate was navigated within the step — just save, stay on step
          await this.save()
          // Force the stepper back
          this.currentStep = null
          await this.$nextTick()
          this.currentStep = previousStep
        } else if (allowNextStep !== true) {
          // Validation failed — show the error and force the stepper back.
          // Same null-reset trick as above to guarantee Vuetify re-renders.
          this.stepErrorMessage = allowNextStep
          this.$refs.messageBox.show()
          this.currentStep = null
          await this.$nextTick()
          this.currentStep = previousStep
        } else {
          // Validation passed — allow the forward navigation and persist.
          this.currentStep = step
          await this.save()
        }
      } else {
        // Moving backward or staying on the same step — no validation needed.
        this.currentStep = step
        await this.save()
      }
    },
    markAllStepsAsCompleted () {
      // Once the deal creation type is selected,
      // all the steps become available.

      // The backend will ensure all the required fields
      // are filled before allowing the user to submit the deal.

      // Therefore, the user can navigate freely between the steps without
      // having to complete them in order.
      for (const step in this.stepStatuses) {
        this.stepStatuses[step] = true
      }

      this.nextStep()
    },
    closeErrorMessage () {
      this.showErrorMessage = false
    },
    validateServerResponse (response) {
      try {
        if (!!response.data.validationErrors &&
          response.data.validationErrors.length > 0
        ) {
          this.errorMessages = response.data.validationErrors
          this.showErrorMessage = true
          return false
        }
        return true
      } catch {
        return false
      }
    },
    async submitDealHandler () {
      await this.submitDeal()
        .then((response) => {
          if (this.validateServerResponse(response)) {
            this.exit()
          }
        })
    },
    async approveDealHandler () {
      this.approveDeal()
        .then((response) => {
          if (this.validateServerResponse(response)) {
            this.exit()
          }
        })
    },
    async rejectDealHandler (notes) {
      this.setRejectionComments(notes)
      await this.rejectDeal()
        .then((response) => {
          if (this.validateServerResponse(response)) {
            this.exit()
          }
        })
    },
    async save () {
      if (!this.isReadOnlyAccess) {
        await this.saveOrUpdateDeal()
      }
    },
    async saveAndExit () {
      await this.save()
      this.exit()
    },
    exit () {
      this.$router.push({ name: placementsRoute.name })
    },
    showJobOpeningInfoModal () {
      this.$refs.ZRJobOpeningsInfoModal.showModal()
    },
    scrollStepperLeft () {
      this.$refs.stepperScroll.scrollBy({ left: -200, behavior: 'smooth' })
    },
    scrollStepperRight () {
      this.$refs.stepperScroll.scrollBy({ left: 200, behavior: 'smooth' })
    },

    // Steps' Methods
    // 1 - Deal Creation Type Methods
    ...dealCreationTypeMethods,

    // 1.1 - Zoho Import Methods
    ...zohoImportMethods,

    // 2 - Deal Assignment Type Methods
    ...dealAssignmentTypeMethods,

    // 3 - Deal Client Methods
    ...dealClientMethods,

    // 4. Deal Project Methods
    ...dealProjectMethods,

    // 5 - Deal Candidate Methods
    ...dealCandidateMethods,

    // 6.1 - Deal Contractor & Deal Project Methods
    ...dealContractDetailsMethods,

    // 6.2 - Deal Direct Placement Methods
    ...dealPermPlacementMethods,

    // 7. Deal Summary Methods
    ...dealSummaryMethods
  },
  async mounted () {
    // If there is a dealId, user is editing an
    // existing deal
    if (this.dealIdParameter) {
      this.getDeal(this.dealIdParameter).then(() => {
        if (this.deal.statusId !== dealStatuses.InProgress) {
          this.currentStep = this.SUMMARY
        }
        this.markAllStepsAsCompleted()

        // When the user selects the Contract Type, the application will get the
        // settings. However, that event is not triggered when loading an existing deal,
        // that is why we need to get the settings here.
        this.getSettings()
      })
    } else {
      // Clear the deal data
      this.clearDeal()
    }
  }
}
</script>

<style scoped>
.stepper-header-container {
  display: flex;
  align-items: center;
}
.stepper-scroll-wrapper {
  overflow-x: auto;
  flex: 1;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.stepper-scroll-wrapper::-webkit-scrollbar {
  display: none;
}
/* Override Vuetify's overflow:hidden on the header so it can expand beyond the container */
.stepper-scroll-wrapper ::v-deep .v-stepper__header {
  overflow: visible;
  min-width: max-content;
}
/* Prevent step labels from shrinking — give each step a fixed minimum width */
.stepper-scroll-wrapper ::v-deep .v-stepper__step {
  flex: 0 0 auto;
  min-width: 120px;
}
/* Prevent dividers from growing too wide */
.stepper-scroll-wrapper ::v-deep .v-stepper__header .v-divider {
  flex: 0 0 24px;
  min-width: 24px;
}
</style>
